import { LoginState, URLParameters } from '../../types';
import queryString from 'query-string';
import { getCurrentCloudType } from '../env';

export type AtlassianAuthenticationLoginParams = {
  username: string;
  password: string;
  state: LoginState;
  query: string;
};

export enum LoginFailureReason {
  AccessDenied = 'access_denied',
  PasswordLeaked = 'password_leaked',
  BlockedUser = 'blocked_user',
  TooManyAttempts = 'too_many_attempts',
  TooManyLogins = 'too_many_logins',
}

export enum LoginError {
  PreconditionFailed = 'precondition_failed',
  UnexpectedError = 'unexpected_error',
}

type LoginSuccessResponse = {
  redirectUri: string;
};

type LoginFailureResponse =
  | { error: 'access_denied' }
  | { error: 'password_leaked' }
  | { error: 'blocked_user' }
  | { error: 'too_many_attempts' }
  | { error: 'too_many_logins' }
  | { error: 'mfa_required'; token: string };

export type LoginFailure = { kind: 'failure'; reason: LoginFailureReason };
export type LoginSuccess = { kind: 'success'; redirectUri: string };

function failure(reason: LoginFailureReason): LoginFailure {
  return { kind: 'failure', reason };
}

function success(redirectUri: string): LoginSuccess {
  return { kind: 'success', redirectUri };
}

export async function login({
  state,
  username,
  password,
  query,
}: AtlassianAuthenticationLoginParams): Promise<LoginFailure | LoginSuccess> {
  const isCommercial = getCurrentCloudType() === 'commercial';
  const token = isCommercial
    ? await import('@castleio/castle-js')
        .then(async ({ createRequestToken }) => await createRequestToken())
        .catch(() => '')
    : 'dummy-token'; // a token is expected by id-auth in IC and disabled entirely within FedRAMP
  return fetch(`/rest/authenticate${query}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password, state, token }),
  }).then(response => {
    switch (response.status) {
      case 200:
        return response.json().then((r: LoginSuccessResponse) => success(r.redirectUri));
      case 403:
        return response.json().then((r: LoginFailureResponse) => {
          switch (r.error) {
            case 'access_denied':
              return failure(LoginFailureReason.AccessDenied);
            case 'blocked_user':
              return failure(LoginFailureReason.BlockedUser);
            case 'password_leaked':
              return failure(LoginFailureReason.PasswordLeaked);
            case 'too_many_attempts':
              return failure(LoginFailureReason.TooManyAttempts);
            default:
              return Promise.reject(new Error(LoginError.UnexpectedError));
          }
        });
      case 429:
        return response
          .json()
          .then((r: LoginFailureResponse) =>
            r.error === 'too_many_logins'
              ? failure(LoginFailureReason.TooManyLogins)
              : Promise.reject(new Error(LoginError.UnexpectedError))
          );
      case 412:
        return response.json().then((r: LoginFailureResponse) => {
          switch (r.error) {
            case 'mfa_required': {
              const currentQueryParams: URLParameters = queryString.parse(window.location.search);
              currentQueryParams.transactionToken = r.token;
              const currentQueryString = queryString.stringify(currentQueryParams);
              return success(`/login/mfa?${currentQueryString}`);
            }
            default:
              return Promise.reject(new Error(LoginError.PreconditionFailed));
          }
        });
      default:
        return Promise.reject(new Error(LoginError.UnexpectedError));
    }
  });
}
